import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Tabs } from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import useApi from 'vl-common/src/hooks/useApi';
import CaseRequests, { RequestDetails } from 'vl-common/src/components/TimeLine/CaseRequests';
import CaseRequestAudit from 'vl-common/src/components/TimeLine/CaseRequestAudit';
import { errorModal } from '../../Modals';
import CancelRequestModal from './CancelRequestModal';
import useSystemType from 'vl-core/hooks/useSystemType';

interface Investigation {
  ireq_id: string;
  appointment_date: string;
  service_type_code: string;
  ireq_state_code: string;
  group_name: string;
  hospital_name: string;
  postcode: string;
  appt_id: string;
}

interface Props {
  case_id: string;
  onChangeInvestigation: (investigation: unknown) => void;
  show: boolean;
  onClose: () => void;
  title: string;
}

const RequestManagerModal = ({ case_id, onChangeInvestigation, show, onClose, title }: Props) => {
  const { t } = useTranslation('common');
  const systemType = useSystemType();
  const { Api, toasted } = useApi();
  const { TabPane: AntTabPane } = Tabs;
  const queryClient = useQueryClient();
  const [showCancelRequestModal, setShowCancelRequestModal] = useState(false);
  const [ireqId, setIreqId] = useState<string>('');

  const {
    data: investigations = [],
    isLoading: investigationsLoading,
    refetch: refetchInvestigations
  } = useQuery({
    queryKey: ['investigations', case_id],
    queryFn: async () => {
      const response = await Api.clinicians.getInvreq({ case_id });
      return response;
    }
  });

  const { data: audit = [], isLoading: auditIsLoading } = useQuery({
    queryKey: ['audit', case_id],
    queryFn: async () => {
      const response = await Api.admin.getInvestigationAudit(case_id);
      const data = response?.data?.invreq_audit?.reverse() || [];
      return data;
    },
    enabled: systemType === 'vl-admin'
  });

  const { data: requestStatusOptions = [], isLoading: requestStatusOptionsIsLoading } = useQuery({
    queryKey: ['metaMisc'],
    queryFn: async () => {
      const response = await Api.meta.getMetaMisc();
      const data = response?.data?.meta[0] || [];
      return data;
    }
  });

  const { mutate: setStatus } = useMutation({
    mutationFn: ({ status, ireq_id }: { status: string; ireq_id: string }) => {
      const response = Api.admin.updateInvestigationStatus(ireq_id, status);
      return response;
    },
    onSuccess: () => {
      queryClient
        .invalidateQueries({
          queryKey: ['investigations', case_id]
        })
        .then();
      queryClient
        .invalidateQueries({
          queryKey: ['investigations audit', case_id]
        })
        .then();
    },
    onError: (error: any) => {
      const { response } = error;
      if (response?.data?.message) {
        errorModal({ title: 'An error occurred', content: response.data.message });
      } else {
        toasted(error);
      }
    }
  });

  const { mutate: cancelRequest } = useMutation({
    mutationFn: ({ ireq_id, cancel_code }: { ireq_id: string; cancel_code: string }) => {
      const response = Api.admin.cancelInvestigationRequest(ireq_id, cancel_code);
      return response;
    },
    onSuccess: () => {
      queryClient
        .invalidateQueries({
          queryKey: ['investigations', case_id]
        })
        .then();
      queryClient
        .invalidateQueries({
          queryKey: ['investigations audit', case_id]
        })
        .then();
    },
    onError: (error: any) => {
      const { response } = error;
      if (response?.data?.message) {
        errorModal({ title: 'An error occurred', content: response.data.message });
      } else {
        toasted(error);
      }
    }
  });

  const isLoading = investigationsLoading || auditIsLoading || requestStatusOptionsIsLoading;

  const requests: RequestDetails[] = investigations?.data?.invreqs
    ? investigations.data.invreqs.map(
        ({
          ireq_id,
          appointment_date,
          service_type_code,
          ireq_state_code,
          group_name,
          hospital_name,
          postcode,
          appt_id
        }: Investigation) => ({
          id: ireq_id,
          date: appointment_date,
          description: `A ${service_type_code} by ${group_name} at ${hospital_name}, ${postcode}`,
          status: ireq_state_code,
          appt_id
        })
      )
    : [];

  const onChangeProvider = useCallback(
    ({ id }: { id: string }) => {
      onChangeInvestigation(investigations.data?.invreqs.find((invreq: any) => invreq.ireq_id === id));
      refetchInvestigations();
    },
    [onChangeInvestigation, investigations]
  );

  async function onResendRequest({ id: ireq_id }: { id: string }) {
    setStatus({ status: 'RESEND', ireq_id });
  }

  async function onStatusChange(status: string, { id: ireq_id }: RequestDetails) {
    if (status === 'CANCELLED') {
      setIreqId(ireq_id);
      setShowCancelRequestModal(true);
    } else {
      setStatus({ status, ireq_id });
    }
  }

  if (isLoading) {
    return null;
  }

  return (
    <Modal
      style={{ maxHeight: 'calc(100vh - 200px)', minWidth: '75vw' }}
      title={title}
      open={show}
      onCancel={onClose}
      footer={[
        <Button key="submit" type="primary" onClick={onClose} data-testid="close-button">
          {t('Close')}
        </Button>
      ]}
      centered
      data-testid="request-manager-modal"
    >
      <CancelRequestModal
        show={showCancelRequestModal}
        onClose={() => {
          setShowCancelRequestModal(false);
        }}
        onSubmit={(cancel_code: string) => {
          setShowCancelRequestModal(false);
          cancelRequest({ ireq_id: ireqId, cancel_code: cancel_code });
        }}
      />

      <Tabs>
        <AntTabPane tab="Requests" key="1">
          <CaseRequests
            requests={requests}
            onResendRequest={onResendRequest}
            onChangeProvider={onChangeProvider}
            onStatusChange={onStatusChange}
            metaMisc={requestStatusOptions}
          />
        </AntTabPane>
        <AntTabPane tab="Activity" key="2">
          <CaseRequestAudit requests={audit} />
        </AntTabPane>
      </Tabs>
    </Modal>
  );
};

export default RequestManagerModal;
